import { useEffect, useMemo, useRef, useState } from 'react';

import {
  brushPoints,
  compositeFrame,
  ellipsePoints,
  fillPoints,
  getPixel,
  linePoints,
  rectPoints,
  setPixel,
  type PixelDoc,
  type RGBA,
} from '../../lib/pixel-doc.js';
import { fitLevel, stepZoom, usePixel, withCell, withPaletteColor, type PixelSelection } from '../../state/pixel.js';

/** Draws RGBA pixels into a reusable offscreen canvas. */
function toCanvas(px: Uint8ClampedArray, w: number, h: number, into?: HTMLCanvasElement): HTMLCanvasElement {
  const c = into ?? document.createElement('canvas');
  if (c.width !== w || c.height !== h) {
    c.width = w;
    c.height = h;
  }
  c.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(px), w, h), 0, 0);
  return c;
}

interface Stroke {
  base: PixelDoc;
  button: number;
  start: [number, number];
  last: [number, number];
  /** Working copy of the cell for freehand tools. */
  cell: Uint8ClampedArray;
  /** Move tool: the selection at stroke start. */
  sel: PixelSelection | null;
}

let lastPoint: [number, number] | null = null;

/** The pixel art canvas: zoom, pan, onion skin, grid, selection and tools. */
export function PixelCanvas() {
  const doc = usePixel((s) => s.doc);
  const frame = usePixel((s) => s.frame);
  const zoom = usePixel((s) => s.zoom);
  const grid = usePixel((s) => s.grid);
  const onion = usePixel((s) => s.onion);
  const tiled = usePixel((s) => s.tiled);
  const selection = usePixel((s) => s.selection);
  const tool = usePixel((s) => s.tool);
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scratch = useRef<{ frame?: HTMLCanvasElement; onion?: HTMLCanvasElement }>({});
  const [size, setSize] = useState({ w: 600, h: 400 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState<[number, number] | null>(null);
  const stroke = useRef<Stroke | null>(null);
  const panning = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const spaceDown = useRef(false);
  const fitRef = useRef<() => void>(() => {});

  // Track the host size.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // Recenter when a different document opens or the canvas size changes.
  const docKey = doc ? `${usePixel.getState().path}:${doc.width}x${doc.height}` : '';
  useEffect(() => {
    setPan({ x: 0, y: 0 });
    fitZoom();
  }, [docKey]);

  /** Largest whole zoom that shows the whole canvas (leaves room for the AI bar). */
  function fitZoom() {
    const el = hostRef.current;
    const d = usePixel.getState().doc;
    if (!el || !d || !el.clientWidth) return;
    usePixel.getState().set({ zoom: fitLevel(d.width, d.height, el.clientWidth - 48, el.clientHeight - 110) });
  }
  fitRef.current = fitZoom;

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = true;
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '0' && !typing && !e.ctrlKey && !e.metaKey && hostRef.current?.offsetParent) {
        setPan({ x: 0, y: 0 });
        fitRef.current();
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // Composite once per document/frame change (not on every hover).
  const frameImage = useMemo(
    () => (doc ? toCanvas(compositeFrame(doc, frame), doc.width, doc.height, scratch.current.frame) : null),
    [doc, frame],
  );
  const onionImages = useMemo(() => {
    if (!doc || !onion) return [];
    const out: Array<[HTMLCanvasElement, number]> = [];
    if (frame > 0) out.push([toCanvas(compositeFrame(doc, frame - 1), doc.width, doc.height), 0.3]);
    if (frame < doc.cells.length - 1) out.push([toCanvas(compositeFrame(doc, frame + 1), doc.width, doc.height), 0.15]);
    return out;
  }, [doc, frame, onion]);
  if (frameImage) scratch.current.frame = frameImage;

  const origin = doc
    ? { x: Math.round(size.w / 2 - (doc.width * zoom) / 2 + pan.x), y: Math.round(size.h / 2 - (doc.height * zoom) / 2 + pan.y) }
    : { x: 0, y: 0 };

  // Render.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(size.w * dpr));
    canvas.height = Math.max(1, Math.floor(size.h * dpr));
    const g = canvas.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, size.w, size.h);
    if (!doc) return;
    const W = doc.width * zoom;
    const H = doc.height * zoom;
    // Checkerboard behind transparent pixels.
    const check = Math.max(4, Math.min(16, zoom));
    g.save();
    g.beginPath();
    g.rect(origin.x, origin.y, W, H);
    g.clip();
    g.fillStyle = '#2b2d33';
    g.fillRect(origin.x, origin.y, W, H);
    g.fillStyle = '#34363d';
    // Only the visible part of the checkerboard (big canvases zoomed in).
    const y0 = Math.max(0, Math.floor(-origin.y / check) * check);
    const y1 = Math.min(H, size.h - origin.y);
    const x0 = Math.max(0, Math.floor(-origin.x / (check * 2)) * check * 2);
    const x1 = Math.min(W, size.w - origin.x);
    for (let y = y0; y < y1; y += check)
      for (let x = x0 + (Math.floor(y / check) % 2) * check; x < x1; x += check * 2) g.fillRect(origin.x + x, origin.y + y, check, check);
    g.imageSmoothingEnabled = zoom < 1;
    for (const [img, alpha] of onionImages) {
      g.globalAlpha = alpha;
      g.drawImage(img, origin.x, origin.y, W, H);
    }
    g.globalAlpha = 1;
    if (frameImage) g.drawImage(frameImage, origin.x, origin.y, W, H);
    g.restore();

    // Tiled preview: the frame repeated around the canvas shows seams.
    if (tiled && frameImage) {
      g.imageSmoothingEnabled = zoom < 1;
      g.globalAlpha = 0.65;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) if (dx || dy) g.drawImage(frameImage, origin.x + dx * W, origin.y + dy * H, W, H);
      g.globalAlpha = 1;
    }

    // Pixel grid.
    if (grid && zoom >= 6) {
      g.strokeStyle = 'rgba(255,255,255,0.07)';
      g.lineWidth = 1;
      g.beginPath();
      for (let x = 0; x <= doc.width; x++) {
        g.moveTo(origin.x + x * zoom + 0.5, origin.y);
        g.lineTo(origin.x + x * zoom + 0.5, origin.y + H);
      }
      for (let y = 0; y <= doc.height; y++) {
        g.moveTo(origin.x, origin.y + y * zoom + 0.5);
        g.lineTo(origin.x + W, origin.y + y * zoom + 0.5);
      }
      g.stroke();
      // Every 8 / 16 pixels a stronger line helps count.
      const major = doc.width >= 32 || doc.height >= 32 ? 16 : 8;
      g.strokeStyle = 'rgba(255,255,255,0.16)';
      g.beginPath();
      for (let x = major; x < doc.width; x += major) {
        g.moveTo(origin.x + x * zoom + 0.5, origin.y);
        g.lineTo(origin.x + x * zoom + 0.5, origin.y + H);
      }
      for (let y = major; y < doc.height; y += major) {
        g.moveTo(origin.x, origin.y + y * zoom + 0.5);
        g.lineTo(origin.x + W, origin.y + y * zoom + 0.5);
      }
      g.stroke();
    }
    g.strokeStyle = 'rgba(255,255,255,0.25)';
    g.strokeRect(origin.x - 0.5, origin.y - 0.5, W + 1, H + 1);

    // Pivot marker.
    if (doc.pivot) {
      const px = origin.x + doc.pivot[0] * W;
      const py = origin.y + (1 - doc.pivot[1]) * H;
      g.strokeStyle = '#ff5ea8';
      g.beginPath();
      g.moveTo(px - 6, py);
      g.lineTo(px + 6, py);
      g.moveTo(px, py - 6);
      g.lineTo(px, py + 6);
      g.stroke();
    }

    // Selection marquee.
    if (selection) {
      g.save();
      g.setLineDash([4, 3]);
      g.strokeStyle = '#ffffff';
      g.strokeRect(origin.x + selection.x * zoom + 0.5, origin.y + selection.y * zoom + 0.5, selection.w * zoom - 1, selection.h * zoom - 1);
      g.lineDashOffset = 3.5;
      g.strokeStyle = '#000000';
      g.strokeRect(origin.x + selection.x * zoom + 0.5, origin.y + selection.y * zoom + 0.5, selection.w * zoom - 1, selection.h * zoom - 1);
      g.restore();
    }

    // Brush preview.
    if (hover && !stroke.current && ['pencil', 'eraser', 'line', 'rect', 'ellipse'].includes(tool)) {
      const s = usePixel.getState();
      const pts = brushPoints(doc, [hover], tool === 'pencil' || tool === 'eraser' ? s.brush : 1, s.mirrorX, s.mirrorY);
      g.fillStyle = tool === 'eraser' ? 'rgba(255,255,255,0.25)' : `rgba(${s.color[0]},${s.color[1]},${s.color[2]},0.55)`;
      for (const [x, y] of pts) g.fillRect(origin.x + x * zoom, origin.y + y * zoom, zoom, zoom);
    }
  }, [doc, frameImage, onionImages, tiled, zoom, grid, selection, size, origin.x, origin.y, hover, tool]);

  function cellAt(e: React.PointerEvent | React.WheelEvent): [number, number] {
    const rect = canvasRef.current!.getBoundingClientRect();
    return [Math.floor((e.clientX - rect.left - origin.x) / zoom), Math.floor((e.clientY - rect.top - origin.y) / zoom)];
  }

  function paintColor(button: number): RGBA {
    const s = usePixel.getState();
    if (s.tool === 'eraser') return [0, 0, 0, 0];
    return button === 2 ? s.secondary : s.color;
  }

  /** Applies points to the stroke's cell and previews the result. */
  function apply(st: Stroke, pts: Array<[number, number]>, color: RGBA, brush: number) {
    const s = usePixel.getState();
    const d = st.base;
    for (const [x, y] of brushPoints(d, pts, brush, s.mirrorX, s.mirrorY)) setPixel(d, st.cell, x, y, color);
    s.commit(withCell(d, s.frame, s.layer, st.cell), { history: false });
  }

  function shapePoints(st: Stroke, to: [number, number], shift: boolean): Array<[number, number]> {
    const s = usePixel.getState();
    let [x1, y1] = to;
    const [x0, y0] = st.start;
    if (shift && s.tool !== 'line') {
      // Square / circle.
      const d = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
      x1 = x0 + Math.sign(x1 - x0 || 1) * d;
      y1 = y0 + Math.sign(y1 - y0 || 1) * d;
    }
    if (s.tool === 'line') return linePoints(x0, y0, x1, y1);
    if (s.tool === 'rect') return rectPoints(x0, y0, x1, y1, s.filled);
    return ellipsePoints(x0, y0, x1, y1, s.filled);
  }

  function onPointerDown(e: React.PointerEvent) {
    const s = usePixel.getState();
    if (!s.doc) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    if (e.button === 1 || (e.button === 0 && spaceDown.current)) {
      panning.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
      return;
    }
    if (s.playing) s.set({ playing: false });
    const p = cellAt(e);
    const cell = s.doc.cells[s.frame]?.[s.layer];
    if (!cell) return;
    const pick = s.tool === 'picker' || (e.altKey && s.tool !== 'select' && s.tool !== 'move');
    if (pick) {
      const c = getPixel(s.doc, compositeFrame(s.doc, s.frame), p[0], p[1]);
      if (c[3] > 0) s.set(e.button === 2 ? { secondary: c } : { color: c });
      return;
    }
    if (s.tool === 'fill') {
      const color = paintColor(e.button);
      const copy = new Uint8ClampedArray(cell);
      for (const [x, y] of fillPoints(s.doc, cell, p[0], p[1], s.contiguous)) setPixel(s.doc, copy, x, y, color);
      s.commit(withPaletteColor(withCell(s.doc, s.frame, s.layer, copy), color));
      return;
    }
    const st: Stroke = { base: s.doc, button: e.button, start: p, last: p, cell: new Uint8ClampedArray(cell), sel: s.selection };
    stroke.current = st;
    if (s.tool === 'select') {
      s.set({ selection: { x: p[0], y: p[1], w: 1, h: 1 } });
      return;
    }
    if (s.tool === 'pencil' || s.tool === 'eraser') {
      const from = e.shiftKey && lastPoint ? lastPoint : p;
      apply(st, linePoints(from[0], from[1], p[0], p[1]), paintColor(e.button), s.brush);
      lastPoint = p;
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const p = cellAt(e);
    setHover((h) => (h && h[0] === p[0] && h[1] === p[1] ? h : p));
    if (panning.current) {
      const pn = panning.current;
      setPan({ x: pn.px + e.clientX - pn.x, y: pn.py + e.clientY - pn.y });
      return;
    }
    const st = stroke.current;
    const s = usePixel.getState();
    if (!st || !s.doc) return;
    if (p[0] === st.last[0] && p[1] === st.last[1]) return;
    if (s.tool === 'pencil' || s.tool === 'eraser') {
      apply(st, linePoints(st.last[0], st.last[1], p[0], p[1]), paintColor(st.button), s.brush);
      lastPoint = p;
    } else if (s.tool === 'line' || s.tool === 'rect' || s.tool === 'ellipse') {
      st.cell = new Uint8ClampedArray(st.base.cells[s.frame]![s.layer]!);
      apply(st, shapePoints(st, p, e.shiftKey), paintColor(st.button), 1);
    } else if (s.tool === 'select') {
      const x = Math.max(0, Math.min(st.start[0], p[0]));
      const y = Math.max(0, Math.min(st.start[1], p[1]));
      const x2 = Math.min(s.doc.width - 1, Math.max(st.start[0], p[0]));
      const y2 = Math.min(s.doc.height - 1, Math.max(st.start[1], p[1]));
      s.set({ selection: { x, y, w: x2 - x + 1, h: y2 - y + 1 } });
    } else if (s.tool === 'move') {
      const dx = p[0] - st.start[0];
      const dy = p[1] - st.start[1];
      const d = st.base;
      const src = d.cells[s.frame]![s.layer]!;
      const sel = st.sel ?? { x: 0, y: 0, w: d.width, h: d.height };
      const out = new Uint8ClampedArray(src);
      for (let y = sel.y; y < sel.y + sel.h; y++) for (let x = sel.x; x < sel.x + sel.w; x++) setPixel(d, out, x, y, [0, 0, 0, 0]);
      for (let y = sel.y; y < sel.y + sel.h; y++)
        for (let x = sel.x; x < sel.x + sel.w; x++) {
          const c = getPixel(d, src, x, y);
          if (c[3] > 0) setPixel(d, out, x + dx, y + dy, c);
        }
      s.commit(withCell(d, s.frame, s.layer, out), { history: false });
      if (st.sel) s.set({ selection: { ...st.sel, x: st.sel.x + dx, y: st.sel.y + dy } });
    }
    st.last = p;
  }

  function onPointerUp() {
    panning.current = null;
    const st = stroke.current;
    stroke.current = null;
    const s = usePixel.getState();
    if (!st || !s.doc) return;
    if (s.tool === 'select') {
      const sel = s.selection;
      if (sel && sel.w === 1 && sel.h === 1 && st.start[0] === st.last[0] && st.start[1] === st.last[1]) s.set({ selection: null });
      return;
    }
    if (s.doc === st.base) return;
    const color = paintColor(st.button);
    s.commit(withPaletteColor(s.doc, color), { base: st.base });
  }

  function onWheel(e: React.WheelEvent) {
    const s = usePixel.getState();
    if (!s.doc) return;
    const next = stepZoom(s.zoom, e.deltaY < 0 ? 1 : -1);
    if (next === s.zoom) return;
    // Zoom around the cursor.
    const rect = canvasRef.current!.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const fx = (mx - origin.x) / s.zoom;
    const fy = (my - origin.y) / s.zoom;
    const nx = mx - fx * next;
    const ny = my - fy * next;
    setPan({ x: nx - (size.w / 2 - (s.doc.width * next) / 2), y: ny - (size.h / 2 - (s.doc.height * next) / 2) });
    s.set({ zoom: next });
  }

  const cursor = tool === 'move' ? 'move' : tool === 'picker' ? 'copy' : 'crosshair';
  return (
    <div className="pixel-canvas-host" ref={hostRef}>
      <canvas
        ref={canvasRef}
        className="pixel-canvas"
        style={{ width: size.w, height: size.h, cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(null)}
        onWheel={onWheel}
        onContextMenu={(e) => e.preventDefault()}
      />
      {doc && hover && hover[0] >= 0 && hover[1] >= 0 && hover[0] < doc.width && hover[1] < doc.height && (
        <span className="pixel-coords mono">
          {hover[0]}, {hover[1]}
          {selection ? ` · sel ${selection.w}×${selection.h}` : ''}
        </span>
      )}
    </div>
  );
}

/** Renders one frame of a document into a small canvas (timeline thumbnails, previews). */
export function FrameThumb({ doc, frame, size = 40, className }: { doc: PixelDoc; frame: number; size?: number; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    toCanvas(compositeFrame(doc, frame), doc.width, doc.height, c);
  }, [doc, frame]);
  const scale = size / Math.max(doc.width, doc.height);
  return (
    <canvas
      ref={ref}
      className={`pixel-thumb${className ? ` ${className}` : ''}`}
      style={{ width: Math.max(1, Math.round(doc.width * scale)), height: Math.max(1, Math.round(doc.height * scale)) }}
    />
  );
}
